import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { getFirebaseClients } from '../firebase';
import { toIsoString } from '../schemas/common';
import { parseDocs } from './parseDocs';
import {
  formateurInputSchema,
  formateurSchema,
  type Formateur,
  type FormateurInput,
} from '../schemas/formateur';

const COLLECTION = 'formateurs';

function db() {
  return getFirebaseClients().firestore;
}

function parse(raw: unknown, id: string): Formateur {
  const data = raw as Record<string, unknown>;
  return formateurSchema.parse({
    ...data,
    id,
    createdAt: toIsoString(data.createdAt),
    updatedAt: toIsoString(data.updatedAt),
  });
}

export async function listFormateurs(): Promise<Formateur[]> {
  // Sorted entirely client-side because a Firestore-level `orderBy('displayOrder')`
  // would filter out legacy docs missing the field. The collection is tiny
  // (single-digit) so the cost is negligible. Schema defaults displayOrder to
  // 50 for any doc that lacks it.
  const snap = await getDocs(collection(db(), COLLECTION));
  const list = parseDocs(snap.docs, parse, COLLECTION);
  return list.sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
}

export async function getFormateur(id: string): Promise<Formateur | null> {
  const snap = await getDoc(doc(db(), COLLECTION, id));
  if (!snap.exists()) return null;
  return parse(snap.data(), snap.id);
}

export async function createFormateur(input: FormateurInput): Promise<string> {
  const parsed = formateurInputSchema.parse(input);
  const now = new Date().toISOString();
  await setDoc(doc(db(), COLLECTION, parsed.slug), {
    ...parsed,
    createdAt: now,
    updatedAt: now,
  });
  return parsed.slug;
}

export async function updateFormateur(id: string, patch: Partial<FormateurInput>): Promise<void> {
  await updateDoc(doc(db(), COLLECTION, id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteFormateur(id: string): Promise<void> {
  await deleteDoc(doc(db(), COLLECTION, id));
}
